import { ContentStatus } from "../../models/enums/content-status.enum";
import { ContentType } from "../../models/enums/content-type.enum";
import { ActivityContent, ActivityQuestion, ActivityQuestionOption, CourseContent, RichTextContent } from "../../models/interfaces/content.interface";
import { richTextMarkdownContentMock } from "../../../../assets/mocks/rich text/rich-text-content.mock";
import {
  capacitacaoPontualParaContinuaMarkdown,
  medirImpactoCapacitacaoMarkdown,
  oQueECulturaAprendizagemMarkdown,
  painelIndicadoresCapacitacaoMarkdown,
  praticasEngajamentoCapacitacaoMarkdown,
  trilhasMicrolearningEngajamentoMarkdown,
} from "../../../../assets/mocks/rich text/capacitacao-corporativa-content.mock";

const TENANT_ID = 'tenant-uuid';
const BASE_COURSE_ID = 'course-uuid-4';

const buildQuestionOptions = (
  questionId: string,
  optionTexts: string[],
  correctOptionIndex?: number,
): ActivityQuestionOption[] => (
  optionTexts.map((text, index) => ({
    id: `${questionId}-option-${index + 1}`,
    text,
    ...(typeof correctOptionIndex === 'number' ? { isCorrect: index === correctOptionIndex } : {}),
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  }))
);

const defaultOptionTexts = [
  'Representa corretamente a aplicação prática do conceito apresentado.',
  'Foca em uma tarefa operacional sem relação direta com o tema principal.',
  'Descreve uma ação isolada que ignora indicadores e acompanhamento.',
  'Apresenta uma decisão sem alinhamento com os objetivos do módulo.',
];

type ActivityQuestionSeed = {
  question: string;
  options: [string, string, string, string];
};

const buildActivityQuestions = (
  contentId: string,
  prompts: Array<string | ActivityQuestionSeed>,
  correctOptionIndex?: number,
): ActivityQuestion[] => (
  prompts.map((prompt, index) => {
    const questionId = `${contentId}-question-${index + 1}`;
    const question = typeof prompt === 'string' ? prompt : prompt.question;
    const optionTexts = typeof prompt === 'string' ? defaultOptionTexts : prompt.options;

    return {
      id: questionId,
      question,
      ...(typeof correctOptionIndex === 'number' ? { correctOptionIndex } : {}),
      options: buildQuestionOptions(questionId, optionTexts, correctOptionIndex),
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
    };
  })
);

const buildActivityContent = (
  id: string,
  moduleId: string,
  title: string,
  order: number,
  status: ContentStatus,
  prompts: Array<string | ActivityQuestionSeed>,
  options?: { courseId?: string; correctOptionIndex?: number },
): ActivityContent => {
  const questions = buildActivityQuestions(id, prompts, options?.correctOptionIndex);

  return {
    id,
    tenantId: TENANT_ID,
    courseId: options?.courseId ?? BASE_COURSE_ID,
    moduleId,
    title,
    order,
    type: ContentType.ACTIVITY,
    mandatoryProgress: 100,
    status,
    questionsIds: questions.map((question) => question.id),
    questions,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  };
};

const module1EvaluationPrompts: string[] = [
  'Qual prática fortalece a conexão entre estratégia organizacional e gestão de pessoas?',
  'Como o onboarding contribui para reduzir a rotatividade em novos times?',
  'Qual decisão melhora a comunicação entre liderança e colaboradores?',
  'Qual ação apoia o desenvolvimento contínuo de competências no time?',
  'Como definir metas de desempenho com maior clareza para a equipe?',
  'Qual abordagem aumenta o engajamento em ciclos de feedback?',
  'Como alinhar plano de desenvolvimento individual com objetivos do negócio?',
  'Qual critério ajuda a priorizar iniciativas de clima organizacional?',
  'Como mapear lacunas de habilidades antes de uma mudança estrutural?',
  'Qual prática melhora a avaliação de resultados em gestão de pessoas?',
];

const module2EvaluationPrompts: string[] = [
  'Qual ação representa liderança situacional em um time híbrido?',
  'Como ajustar o estilo de liderança para perfis com diferentes níveis de autonomia?',
  'Qual estratégia ajuda a manter cultura organizacional consistente em trabalho remoto?',
  'Como medir evolução de carreira com foco em competências comportamentais?',
  'Qual prática fortalece segurança psicológica durante reuniões de alinhamento?',
  'Como conduzir delegação sem perder visibilidade sobre entregas críticas?',
  'Qual indicador sinaliza necessidade de intervenção de desenvolvimento em líderes?',
  'Como estruturar um plano de sucessão para funções-chave do time?',
  'Qual abordagem melhora a colaboração entre áreas com objetivos distintos?',
  'Como equilibrar reconhecimento individual e resultado coletivo na gestão de desempenho?',
];

const module3EvaluationPrompts: string[] = [
  'Qual métrica permite acompanhar a eficiência de uma iniciativa de melhoria contínua?',
  'Como definir KPI com foco em resultado e não apenas em atividade?',
  'Qual prática reduz o risco de interpretar indicadores fora de contexto?',
  'Como priorizar ações quando vários indicadores apresentam queda simultânea?',
  'Qual passo é essencial para criar plano de ação orientado por dados?',
  'Como validar se uma melhoria implantada gerou impacto sustentável?',
  'Qual estrategia aumenta transparência no acompanhamento de metas?',
  'Como comunicar resultados de desempenho para público executivo?',
  'Qual critério ajuda a revisar metas após mudanças no cenário do negócio?',
  'Como transformar aprendizados de indicadores em padrões operacionais?',
];

const poModule1EvaluationQuestions: ActivityQuestionSeed[] = [
  {
    question: 'O que caracteriza uma cultura de aprendizagem nas organizações?',
    options: [
      'A capacidade de aprender e se adaptar de forma contínua, integrada à rotina e à estratégia.',
      'A oferta de um treinamento anual obrigatório para todos os colaboradores.',
      'A substituição de líderes por plataformas digitais de conteúdo.',
      'A redução de metas operacionais para priorizar apenas cursos teóricos.',
    ],
  },
  {
    question: 'Qual é a principal diferença entre treinamento isolado e aprendizagem contínua?',
    options: [
      'O treinamento isolado é pontual; a aprendizagem contínua é um processo permanente de desenvolvimento.',
      'O treinamento isolado sempre gera mais impacto de negócio.',
      'A aprendizagem contínua dispensa qualquer tipo de avaliação.',
      'Não há diferença relevante entre os dois modelos.',
    ],
  },
  {
    question: 'Por que a educação corporativa deve estar alinhada aos objetivos do negócio?',
    options: [
      'Para desenvolver competências que realmente impactam performance e resultados.',
      'Para aumentar apenas a quantidade de horas de treinamento registradas.',
      'Para eliminar a necessidade de acompanhamento da liderança.',
      'Para padronizar o mesmo curso para todas as áreas, sem exceções.',
    ],
  },
  {
    question: 'Qual prática fortalece a cultura de aprendizagem no dia a dia?',
    options: [
      'Liderança que aprende em público, dá feedback e reconhece quem aplica o conhecimento.',
      'Bloquear o acesso a conteúdos fora do horário comercial.',
      'Avaliar apenas presença em salas de aula presenciais.',
      'Evitar trilhas para não limitar a autonomia do colaborador.',
    ],
  },
  {
    question: 'O que significa tratar capacitação como estratégia, e não como evento?',
    options: [
      'Estruturar um sistema contínuo de desenvolvimento ligado a competências e resultados.',
      'Organizar um workshop único no fim de cada trimestre.',
      'Medir sucesso apenas pelo número de certificados emitidos.',
      'Delegar toda a responsabilidade de aprendizado somente ao colaborador.',
    ],
  },
  {
    question: 'Qual risco a empresa corre ao negligenciar a educação corporativa?',
    options: [
      'Obsolecer competências, processos e competitividade diante das mudanças do mercado.',
      'Aumentar excessivamente o engajamento das equipes.',
      'Reduzir demais a rotatividade de talentos.',
      'Criar excesso de indicadores de impacto no negócio.',
    ],
  },
  {
    question: 'Como a liderança contribui para a cultura de aprendizagem?',
    options: [
      'Identificando lacunas, protegendo tempo para aprender e reforçando a aplicação prática.',
      'Impedindo que o time compartilhe aprendizados entre áreas.',
      'Priorizando apenas treinamentos sem conexão com a rotina.',
      'Substituindo feedback por avaliações anuais genéricas.',
    ],
  },
  {
    question: 'Qual elemento diferencia uma trilha de uma simples grade de cursos?',
    options: [
      'A trilha organiza uma sequência com propósito e competência-alvo claros.',
      'A grade sempre inclui mentoria e prática supervisionada.',
      'A trilha remove qualquer necessidade de avaliação.',
      'A grade define automaticamente o impacto no negócio.',
    ],
  },
  {
    question: 'O que a aprendizagem contínua prioriza em relação ao treinamento pontual?',
    options: [
      'Aplicação no trabalho, revisão constante e impacto sustentado.',
      'Maior duração de cada aula presencial.',
      'Conteúdos sem relação com os desafios da área.',
      'Conclusão rápida sem acompanhamento posterior.',
    ],
  },
  {
    question: 'Qual indicador melhor representa maturidade em cultura de aprendizagem?',
    options: [
      'Evidências de aplicação do conhecimento e evolução de competências críticas.',
      'Número absoluto de slides produzidos pelo time de T&D.',
      'Quantidade de salas físicas disponíveis para treinamento.',
      'Frequência de reuniões sem pauta de desenvolvimento.',
    ],
  },
];

const poModule2EvaluationQuestions: ActivityQuestionSeed[] = [
  {
    question: 'O que é aprendizagem no fluxo de trabalho?',
    options: [
      'Aprender no contexto da rotina, com recursos disponíveis no momento da necessidade.',
      'Separar totalmente o estudo do ambiente de trabalho.',
      'Realizar apenas cursos longos fora do horário comercial.',
      'Substituir a prática por avaliações teóricas mensais.',
    ],
  },
  {
    question: 'Como o microlearning contribui para o engajamento em capacitação?',
    options: [
      'Entrega pílulas curtas e aplicáveis, que cabem melhor na rotina do colaborador.',
      'Aumenta a carga horária obrigatória de cada módulo.',
      'Elimina a necessidade de prática e feedback.',
      'Padroniza um único formato longo para todos os temas.',
    ],
  },
  {
    question: 'Qual é o papel da liderança no desenvolvimento das equipes?',
    options: [
      'Facilitar, acompanhar e conectar o aprendizado aos desafios reais do time.',
      'Centralizar todo o conteúdo sem envolver o colaborador.',
      'Evitar reconhecimento para não gerar expectativas.',
      'Tratar capacitação como responsabilidade exclusiva do RH.',
    ],
  },
  {
    question: 'O que define uma trilha de aprendizagem com propósito?',
    options: [
      'Uma jornada sequenciada que combina necessidades da empresa e objetivos do colaborador.',
      'Uma lista aleatória de vídeos sem critério de competência.',
      'Um único conteúdo genérico para todos os cargos.',
      'Um ranking competitivo sem meta de desenvolvimento.',
    ],
  },
  {
    question: 'Qual prática aumenta a aplicação do conteúdo no dia a dia?',
    options: [
      'Combinar aprendizado com prática imediata e feedback da liderança.',
      'Adiar a aplicação até o próximo ciclo anual de avaliação.',
      'Remover exemplos práticos para reduzir a complexidade.',
      'Priorizar apenas leitura passiva sem exercícios.',
    ],
  },
  {
    question: 'Como a aprendizagem social apoia o engajamento?',
    options: [
      'Acelera a curva de aprendizado por meio de mentorias, pares e troca de experiências.',
      'Substitui qualquer necessidade de conteúdo estruturado.',
      'Reduz a colaboração entre áreas diferentes.',
      'Impede que líderes participem do desenvolvimento do time.',
    ],
  },
  {
    question: 'Quando a gamificação ajuda de fato na capacitação?',
    options: [
      'Quando está ligada a objetivos reais de desenvolvimento, e não só a rankings vazios.',
      'Quando o único objetivo é competir por pontos sem contexto.',
      'Quando elimina feedbacks qualitativos da liderança.',
      'Quando substitui completamente as trilhas de competência.',
    ],
  },
  {
    question: 'Qual ação comunica melhor o valor de um programa de desenvolvimento?',
    options: [
      'Explicar o “porquê” do programa e o benefício para carreira e resultado.',
      'Enviar apenas o link do curso sem contexto.',
      'Ocultar os objetivos para gerar surpresa.',
      'Focar só na obrigatoriedade de conclusão.',
    ],
  },
  {
    question: 'Por que disponibilizar conteúdo no momento da necessidade importa?',
    options: [
      'Aumenta a chance de aplicação imediata na decisão ou tarefa real.',
      'Garante que o colaborador nunca revise o conteúdo depois.',
      'Elimina a importância de trilhas e competências.',
      'Reduz o alinhamento com os desafios da operação.',
    ],
  },
  {
    question: 'Qual checklist ajuda gestores a sustentar engajamento em capacitação?',
    options: [
      'Comunicar o propósito, proteger tempo para aprender, reconhecer aplicação e pedir feedback.',
      'Aumentar a quantidade de cursos sem priorização.',
      'Medir apenas presença e ignorar resultados.',
      'Evitar qualquer acompanhamento após a inscrição.',
    ],
  },
];

const poModule3EvaluationQuestions: ActivityQuestionSeed[] = [
  {
    question: 'Por que medir só conclusão de curso é insuficiente?',
    options: [
      'Porque não mostra se o conhecimento foi aplicado e gerou impacto no negócio.',
      'Porque conclusão de curso nunca deve ser registrada.',
      'Porque avaliações práticas são proibidas em educação corporativa.',
      'Porque indicadores de negócio não se relacionam com capacitação.',
    ],
  },
  {
    question: 'Qual indicador pertence à camada de comportamento?',
    options: [
      'Aplicação do conteúdo na rotina e mudança de prática observada pela liderança.',
      'Número de certificados emitidos no semestre.',
      'Quantidade de salas de treinamento disponíveis.',
      'Tempo médio de carregamento da plataforma.',
    ],
  },
  {
    question: 'Como reportar impacto de capacitação para a liderança?',
    options: [
      'Conectar aprendizagem a resultados concretos, como redução de retrabalho ou ganho de performance.',
      'Apresentar apenas a lista de inscritos no programa.',
      'Mostrar só a quantidade de slides por módulo.',
      'Evitar comparar períodos antes e depois da trilha.',
    ],
  },
  {
    question: 'O que um painel de L&D deve priorizar?',
    options: [
      'Poucos indicadores relevantes de participação, aprendizagem, engajamento e impacto.',
      'O maior número possível de métricas, mesmo sem decisão associada.',
      'Apenas dados de infraestrutura de TI.',
      'Somente feedbacks anônimos sem vínculo com competências.',
    ],
  },
  {
    question: 'Qual passo inicia o ciclo de melhoria contínua em capacitação?',
    options: [
      'Diagnosticar lacunas de competência alinhadas ao negócio.',
      'Emitir certificados antes de definir objetivos.',
      'Criar rankings sem mapear necessidades.',
      'Encerrar trilhas sem validar aplicação prática.',
    ],
  },
  {
    question: 'Como cruzar dados de aprendizagem com performance da área?',
    options: [
      'Comparar evolução de indicadores de negócio com progresso e aplicação nas trilhas.',
      'Ignorar dados de performance para não complexificar o relatório.',
      'Usar apenas notas de satisfação sem contexto operacional.',
      'Separar definitivamente RH e áreas de resultado.',
    ],
  },
  {
    question: 'Qual prática evita armadilhas na mensuração de capacitação?',
    options: [
      'Escolher KPIs acionáveis, comparar períodos e envolver líderes na leitura dos resultados.',
      'Medir somente presença e volume de conteúdos publicados.',
      'Trocar os indicadores toda semana sem critério.',
      'Evitar metas claras para não gerar cobrança.',
    ],
  },
  {
    question: 'O que significa validar impacto com evidências?',
    options: [
      'Comprovar, com dados e feedback, que o aprendizado gerou mudança real.',
      'Assumir impacto apenas pela conclusão do módulo.',
      'Substituir indicadores por opiniões isoladas sem registro.',
      'Encerrar o programa sem revisão de conteúdo.',
    ],
  },
  {
    question: 'Por que a aprendizagem contínua mantém relevância profissional?',
    options: [
      'Porque permite aprender, desaprender e se adaptar à velocidade das mudanças.',
      'Porque elimina a necessidade de atualizar competências técnicas.',
      'Porque garante estabilidade total das ferramentas de trabalho.',
      'Porque substitui a curiosidade por planos fixos e imutáveis.',
    ],
  },
  {
    question: 'Qual resultado esperado de um ciclo de melhoria bem aplicado?',
    options: [
      'Desenvolvimento com evidência, menos “treinar por treinar” e mais impacto sustentável.',
      'Aumento de cursos sem conexão com a estratégia.',
      'Redução do acompanhamento da liderança sobre o time.',
      'Substituição total de indicadores por percepção informal.',
    ],
  },
];

const PO_COURSE_ID = 'course-uuid-po';
const MODULE_PO_1 = 'module-po-1';
const MODULE_PO_2 = 'module-po-2';
const MODULE_PO_3 = 'module-po-3';

export const richTextContentMock: RichTextContent = {
  id: 'content-module-1-class-2',
  tenantId: TENANT_ID,
  courseId: BASE_COURSE_ID,
  moduleId: 'module-1',
  title: 'Processos de gestão de pessoas e subsistemas de Recursos Humanos',
  order: 2,
  type: ContentType.RICH_TEXT,
  mandatoryProgress: 100,
  status: ContentStatus.INCOMPLETE,
  content: richTextMarkdownContentMock,

  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
};

const baseCourseContents: CourseContent[] = [
  {
    id: 'content-module-1-class-1',
    tenantId: TENANT_ID,
    courseId: BASE_COURSE_ID,
    moduleId: 'module-1',
    title: 'Gestão de Pessoas',
    order: 1,
    type: ContentType.VIDEO,
    mandatoryProgress: 85,
    status: ContentStatus.INCOMPLETE,
    urlFolder: 'assets/mocks/videos',
    manifestPath: 'assets/mocks/videos/teste-video-local.mp4',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  richTextContentMock,
  {
    id: 'content-module-1-class-3',
    tenantId: TENANT_ID,
    courseId: BASE_COURSE_ID,
    moduleId: 'module-1',
    title: 'Detalhamento dos processos de gestão de pessoas',
    order: 3,
    type: ContentType.HTML,
    mandatoryProgress: 100,
    status: ContentStatus.PREPARING,
    urlPath: 'assets/mocks/html/processos-rh',
    entrypoint: 'index.html',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-1-class-4',
      'module-1',
      'Avaliação do módulo 1: Gestão estratégica de pessoas',
      4,
      ContentStatus.COMPLETE,
      module1EvaluationPrompts,
    ),
  },
  {
    id: 'content-module-2-class-1',
    tenantId: TENANT_ID,
    courseId: BASE_COURSE_ID,
    moduleId: 'module-2',
    title: 'Cultura organizacional e liderança',
    order: 1,
    type: ContentType.YOUTUBE,
    mandatoryProgress: 80,
    status: ContentStatus.COMPLETE,
    videoUrl: 'https://www.youtube.com/watch?v=M7lc1UVf-VE',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-2-class-2',
    tenantId: TENANT_ID,
    courseId: BASE_COURSE_ID,
    moduleId: 'module-2',
    title: 'Planejamento de carreira e desenvolvimento',
    order: 2,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: richTextContentMock.content,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-2-class-3',
      'module-2',
      'Avaliação do módulo 2: Liderança e desenvolvimento',
      3,
      ContentStatus.COMPLETE,
      module2EvaluationPrompts,
    ),
  },
  {
    id: 'content-module-3-class-1',
    tenantId: TENANT_ID,
    courseId: BASE_COURSE_ID,
    moduleId: 'module-3',
    title: 'Indicadores e métricas de desempenho',
    order: 1,
    type: ContentType.HTML,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    urlPath: 'assets/mocks/html/indicadores-desempenho',
    entrypoint: 'dashboard.html',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-3-class-2',
      'module-3',
      'Avaliação do módulo 3: Indicadores e melhoria contínua',
      2,
      ContentStatus.INCOMPLETE,
      module3EvaluationPrompts,
    ),
  },
];

const replicatedCourseIds: string[] = [
  'course-uuid-1',
  'course-uuid-2',
  'course-uuid-3',
  'course-uuid-5',
  'course-uuid-6',
  'course-uuid-7',
  'course-uuid-8',
  'course-uuid-9',
  'course-uuid-10',
  'course-uuid-11',
  'course-uuid-12',
];

const replicatedCourseContents: CourseContent[] = replicatedCourseIds.flatMap((courseId) =>
  baseCourseContents.map((content) => ({
    ...content,
    id: `${content.id}-${courseId}`,
    courseId,
    createdAt: new Date(),
    updatedAt: new Date(),
  } as CourseContent)),
);

const poCourseContents: CourseContent[] = [
  {
    id: 'content-module-po-1-class-1',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_1,
    title: 'O que é cultura de aprendizagem',
    order: 1,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: oQueECulturaAprendizagemMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-1-class-2',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_1,
    title: 'Como criar uma cultura de aprendizagem',
    order: 2,
    type: ContentType.YOUTUBE,
    mandatoryProgress: 80,
    status: ContentStatus.COMPLETE,
    videoUrl: 'https://www.youtube.com/watch?v=rtblBR09Koc',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-1-class-3',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_1,
    title: 'Da capacitação pontual à aprendizagem contínua',
    order: 3,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: capacitacaoPontualParaContinuaMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-po-1-class-4',
      MODULE_PO_1,
      'Avaliação: cultura de aprendizagem',
      4,
      ContentStatus.COMPLETE,
      poModule1EvaluationQuestions,
      { courseId: PO_COURSE_ID, correctOptionIndex: 0 },
    ),
  },
  {
    id: 'content-module-po-2-class-1',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_2,
    title: 'Aprendizagem no fluxo de trabalho',
    order: 1,
    type: ContentType.YOUTUBE,
    mandatoryProgress: 80,
    status: ContentStatus.COMPLETE,
    videoUrl: 'https://www.youtube.com/watch?v=5Ev1HD_WD5s',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-2-class-2',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_2,
    title: 'Trilhas, microlearning e engajamento',
    order: 2,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: trilhasMicrolearningEngajamentoMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-2-class-3',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_2,
    title: 'Práticas de engajamento em capacitação',
    order: 3,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: praticasEngajamentoCapacitacaoMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-po-2-class-4',
      MODULE_PO_2,
      'Avaliação: engajamento e desenvolvimento',
      4,
      ContentStatus.COMPLETE,
      poModule2EvaluationQuestions,
      { courseId: PO_COURSE_ID, correctOptionIndex: 0 },
    ),
  },
  {
    id: 'content-module-po-3-class-1',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_3,
    title: 'Como medir o impacto da capacitação',
    order: 1,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: medirImpactoCapacitacaoMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-3-class-2',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_3,
    title: 'Painel de indicadores de capacitação',
    order: 2,
    type: ContentType.RICH_TEXT,
    mandatoryProgress: 100,
    status: ContentStatus.COMPLETE,
    content: painelIndicadoresCapacitacaoMarkdown,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    id: 'content-module-po-3-class-3',
    tenantId: TENANT_ID,
    courseId: PO_COURSE_ID,
    moduleId: MODULE_PO_3,
    title: 'Aprendizagem contínua e relevância profissional',
    order: 3,
    type: ContentType.YOUTUBE,
    mandatoryProgress: 80,
    status: ContentStatus.COMPLETE,
    videoUrl: 'https://www.youtube.com/watch?v=UUDfpXIgtzA',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  },
  {
    ...buildActivityContent(
      'content-module-po-3-class-4',
      MODULE_PO_3,
      'Avaliação: resultados e melhoria contínua',
      4,
      ContentStatus.COMPLETE,
      poModule3EvaluationQuestions,
      { courseId: PO_COURSE_ID, correctOptionIndex: 0 },
    ),
  },
];

export const contentMock: CourseContent[] = [
  ...baseCourseContents,
  ...replicatedCourseContents,
  ...poCourseContents,
];
